import { useRef, type KeyboardEvent } from 'react';
import type { ChatSearchMatchMode } from '@/lib/api';

const MODES: ReadonlyArray<{ value: ChatSearchMatchMode; label: string; hint: string }> = [
  {
    value: 'word',
    label: 'Whole word',
    hint: 'Match complete words, e.g. “deploy” finds “deploys”',
  },
  {
    value: 'contains',
    label: 'Contains',
    hint: 'Match your text anywhere, even mid-word, e.g. “oge” finds “booger” (3+ characters)',
  },
];

interface SearchMatchModeToggleProps {
  value: ChatSearchMatchMode;
  onChange: (mode: ChatSearchMatchMode) => void;
}

/**
 * Segmented control choosing how chat search matches text. Shared by both
 * search panels.
 *
 * Follows the WAI-ARIA radio group pattern: one tab stop (the selected option),
 * and the arrow keys move the selection — wrapping at the ends — with Home/End
 * jumping to the first/last option.
 */
export function SearchMatchModeToggle({ value, onChange }: SearchMatchModeToggleProps) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (index: number) => {
    const next = MODES[(index + MODES.length) % MODES.length];
    onChange(next.value);
    buttonRefs.current[(index + MODES.length) % MODES.length]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        select(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        select(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        select(0);
        break;
      case 'End':
        event.preventDefault();
        select(MODES.length - 1);
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label="Match"
      className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40"
    >
      {MODES.map((mode, index) => {
        const selected = mode.value === value;
        return (
          <button
            key={mode.value}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            title={mode.hint}
            onClick={() => onChange(mode.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={[
              'px-2.5 py-1 text-xs rounded-md transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              selected
                ? 'bg-background text-foreground shadow-sm font-medium'
                : 'text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {mode.label}
          </button>
        );
      })}
    </div>
  );
}
